import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppProvider, useApp } from "./context/AppContext";
import { AgentProvider } from "./context/AgentContext";
import OfflineAlert from "./components/OfflineAlert";
import MobileAlert from "./components/MobileAlert";
import SessionExpiredAlert from "./components/SessionExpiredAlert";
import MaintenancePage from "./pages/MaintenancePage";

// Maintenance kill-switch. Default ON; flip OFF by setting
// REACT_APP_MAINTENANCE_MODE=false in Vercel env and redeploying.
const MAINTENANCE_MODE =
  (process.env.REACT_APP_MAINTENANCE_MODE ?? "true").toLowerCase() !== "false";

// Pages
import AuthPage from "./pages/AuthPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CustomAgentRequest from "./pages/CustomAgentRequest";
import DashboardLayout, {
  DashboardHome,
  ProfilePage,
  HelpPage,
  DocsPage,
  FeedbackPage,
} from "./pages/Dashboard";

// Create Agent Wizard
import CreateAgentLayout from "./pages/CreateAgent/CreateAgentLayout";
import StepName from "./pages/CreateAgent/StepName";
import StepBehavior from "./pages/CreateAgent/StepBehavior";
import StepKnowledge from "./pages/CreateAgent/StepKnowledge";
import StepModel from "./pages/CreateAgent/StepModel";
import StepFeatures from "./pages/CreateAgent/StepFeatures";
import StepTraining from "./pages/CreateAgent/StepTraining";
import StepTest from "./pages/CreateAgent/StepTest";
import StepConnect from "./pages/CreateAgent/StepConnect";
import StepPair from "./pages/CreateAgent/StepPair";
import StepGoLive from "./pages/CreateAgent/StepGoLive";

// Agent Dashboard
import AgentDashboardLayout from "./pages/AgentDashboard/AgentDashboardLayout";
import LocalSimulator from "./pages/AgentDashboard/LocalSimulator";
import Conversations from "./pages/AgentDashboard/Conversations";
import ChatsPage from "./pages/AgentDashboard/ChatsPage";
import Leads from "./pages/AgentDashboard/Leads";
import Appointments from "./pages/AgentDashboard/Appointments";
import Emails from "./pages/AgentDashboard/Emails";
import Knowledge from "./pages/AgentDashboard/Knowledge";
import Settings from "./pages/AgentDashboard/Settings";
import Analytics from "./pages/AgentDashboard/Analytics";

// Loading spinner component for auth check
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, isAuthLoading } = useApp();

  // Show loading while checking auth
  if (isAuthLoading) {
    return <AuthLoadingScreen />;
  }

  if (!user) {
    // Save the intended URL (including hash) for redirect after login
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (currentPath !== '/' && currentPath !== '/dashboard') {
      sessionStorage.setItem('returnTo', currentPath);
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

// Auth Route wrapper (redirect if already logged in)
function AuthRoute({ children }) {
  const { user, isAuthLoading } = useApp();

  // Show loading while checking auth
  if (isAuthLoading) {
    return <AuthLoadingScreen />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Auth */}
      <Route
        path="/"
        element={
          <AuthRoute>
            <AuthPage />
          </AuthRoute>
        }
      />

      {/* Legal Pages (Public) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Public Marketing Pages (SEO) */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="custom-agent" element={<CustomAgentRequest />} />
      </Route>

      {/* Documentation - Full Page (no sidebar) */}
      <Route
        path="/dashboard/docs"
        element={
          <ProtectedRoute>
            <DocsPage />
          </ProtectedRoute>
        }
      />

      {/* Create Agent Wizard */}
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <AgentProvider>
              <CreateAgentLayout />
            </AgentProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="name" replace />} />
        <Route path="name" element={<StepName />} />
        <Route path="behavior" element={<StepBehavior />} />
        <Route path="knowledge" element={<StepKnowledge />} />
        <Route path="model" element={<StepModel />} />
        <Route path="features" element={<StepFeatures />} />
        <Route path="training" element={<StepTraining />} />
        <Route path="test" element={<StepTest />} />
        <Route path="connect" element={<StepConnect />} />
        <Route path="pair" element={<StepPair />} />
        <Route path="golive" element={<StepGoLive />} />
      </Route>

      {/* Agent Dashboard */}
      <Route
        path="/agent/:id"
        element={
          <ProtectedRoute>
            <AgentDashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LocalSimulator />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="leads" element={<Leads />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="emails" element={<Emails />} />
        <Route path="knowledge" element={<Knowledge />} />
        <Route path="settings" element={<Settings />} />
        <Route path="analytics" element={<Analytics />} />
      </Route>

      {/* Individual Chat Page */}
      <Route
        path="/agent/:id/chats/:cid"
        element={
          <ProtectedRoute>
            <ChatsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Short-circuit ALL routes (signed-in or not) when maintenance mode is on.
  // The router is intentionally bypassed so no API calls or auth checks fire.
  if (MAINTENANCE_MODE) {
    return (
      <HelmetProvider>
        <MaintenancePage />
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <AppProvider>
        <OfflineAlert />
        <MobileAlert />
        <Router>
          <SessionExpiredAlert />
          <AppRoutes />
        </Router>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
